import { useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { DateTime } from 'luxon';
import { usePageVisibility } from 'react-page-visibility';
import { useDiariesForDateRangeLazyQuery } from 'graphql/types';
import {
  LabelType,
  DiaryEntry,
  DiariesForDateRangeQuery,
  DiariesForDateRangeQueryVariables,
  DiariesForDateRangeDocument,
  DiaryTaskNameFormat,
  DiaryFragmentFragment
} from 'graphql/types';
import {
  SmartDiaryEntry,
  useSmartTimesheetContext
} from 'modules/smartDiary/smartDiaryNotes';
import { useMessage } from 'modules/intl';
import { getDateTimeFromISO, sortDateTimes } from 'utils/date-time';
import usePersistedCacheQuery from 'modules/common/hooks/usePersistedCacheQuery';
import { useTimesheetFrame } from 'modules/smartDiary/embeddedTimesheet/useTimesheetFrame';
import { DEFAULT_POLLING_INTERVAL } from 'modules/smartDiary/constants';
import { useAutoPilotContext } from 'modules/App/menu/items/Settings/AutoPilotContextProvider';
import { OEFLabelData } from '../type';
import { splitDateRanges } from '../utils/date-range-split';

const defaultDate = DateTime.local().toISODate();
export const pushUncategorisedToEnd = (
  smartEntries: SmartDiaryEntry[]
): SmartDiaryEntry[] => {
  const { categorizedEntries, uncategorisedEntries } = smartEntries.reduce(
    (acc, smartEntry) => {
      if (!smartEntry.client && !smartEntry.project && !smartEntry.task) {
        acc.uncategorisedEntries.push(smartEntry);
      } else {
        acc.categorizedEntries.push(smartEntry);
      }
      return acc;
    },
    {
      categorizedEntries: [],
      uncategorisedEntries: []
    } as {
      categorizedEntries: SmartDiaryEntry[];
      uncategorisedEntries: SmartDiaryEntry[];
    }
  );
  return [...categorizedEntries, ...uncategorisedEntries];
};

export const getSmartDiaryNotes = ({
  diaryEntries,
  notApplicableMessage,
  date,
  taskNameFormat
}: {
  diaryEntries: DiaryEntry[];
  notApplicableMessage?: string;
  date: string;
  taskNameFormat: DiaryTaskNameFormat;
}): SmartDiaryEntry[] => {
  const smartDiaryEntries = diaryEntries.map((diaryEntry) => {
    const smartDiaryEntry: SmartDiaryEntry = {
      id: diaryEntry.id,
      timeSignals: diaryEntry.timeSignals,
      syncStatus: diaryEntry.syncStatus,
      canBeSynced: diaryEntry.canBeSynced,
      hasMissingData: (diaryEntry.errors ?? []).length > 0,
      oefs: [],
      date: date,
      duration: {
        value: diaryEntry.duration
      },
      isClientConfigured: false,
      isProgramConfigured: false,
      lastSyncTime: getDateTimeFromISO(diaryEntry.lastSyncTime),
      taskNameFormat,
      startTime: diaryEntry.startTime || undefined,
      endTime: diaryEntry.endTime || undefined
    };

    diaryEntry.labelValues.forEach((labelValue, _index) => {
      if (labelValue.label) {
        switch (labelValue.label.type) {
          case LabelType.Comment:
            smartDiaryEntry.comment = {
              value: labelValue.value?.stringValue ?? '',
              error: labelValue.error
            };
            return;
          case LabelType.Client:
            if (labelValue.value) {
              smartDiaryEntry.client = {
                id: labelValue.value.idValue,
                value: labelValue.value.stringValue,
                error: labelValue.error
              };
            }
            smartDiaryEntry.isClientConfigured = true;
            return;
          case LabelType.Program:
            if (labelValue.value) {
              smartDiaryEntry.program = {
                id: labelValue.value.idValue,
                value: labelValue.value.stringValue,
                error: labelValue.error
              };
            }
            smartDiaryEntry.isProgramConfigured = true;
            return;
          case LabelType.Project:
            if (labelValue.value) {
              smartDiaryEntry.project = {
                id: labelValue.value.idValue,
                value: labelValue.value.stringValue,
                error: labelValue.error
              };
            }
            return;
          case LabelType.Task:
            if (labelValue.value) {
              smartDiaryEntry.task = {
                id: labelValue.value.idValue,
                value: labelValue.value.stringValue,
                error: labelValue.error
              };
            }
            return;
          case LabelType.Select:
            {
              const oefItem: OEFLabelData = {
                labelId: labelValue.label.id,
                name: labelValue.label.name,
                error: labelValue.error
              };
              if (labelValue.value) {
                oefItem.value = labelValue.value.stringValue;
                oefItem.id = labelValue.value.idValue;
              } else {
                oefItem.value = notApplicableMessage;
              }
              smartDiaryEntry.oefs?.push(oefItem);
            }
            return;
          case LabelType.Text:
            {
              const oefItem: OEFLabelData = {
                labelId: labelValue.label.id,
                name: labelValue.label.name,
                error: labelValue.error
              };
              if (labelValue.value) {
                oefItem.value = labelValue.value.stringValue;
                oefItem.id = `${labelValue.label.id}_${labelValue.label.name}_${_index}`;
              } else {
                oefItem.value = notApplicableMessage;
              }
              smartDiaryEntry.oefs?.push(oefItem);
            }
            return;
          case LabelType.Numeric:
            {
              const oefItem: OEFLabelData = {
                labelId: labelValue.label.id,
                name: labelValue.label.name,
                error: labelValue.error
              };
              if (labelValue.value) {
                oefItem.value = labelValue.value.numericValue?.toString();
                oefItem.id = `${labelValue.label.id}_${labelValue.label.name}_${_index}`;
              } else {
                oefItem.value = notApplicableMessage;
              }
              smartDiaryEntry.oefs?.push(oefItem);
            }
            return;
          case LabelType.Activity:
            if (labelValue.value) {
              smartDiaryEntry.activity = {
                id: labelValue.value.idValue,
                value: labelValue.value.stringValue,
                error: labelValue.error
              };
            }
            return;
          case LabelType.Billing:
            if (labelValue.value) {
              smartDiaryEntry.billing = {
                id: labelValue.value.idValue,
                value: labelValue.value.stringValue,
                error: labelValue.error
              };
            }
            return;
        }
      }
    });

    return smartDiaryEntry;
  });
  return pushUncategorisedToEnd(smartDiaryEntries);
};

const getRecentLastSyncTime = (diaries: DiaryFragmentFragment[]) => {
  const lastSyncTimes = (diaries ?? [])
    .reduce(
      (acc, value) => [
        ...acc,
        value.lastSyncTime ? DateTime.fromISO(value.lastSyncTime) : undefined
      ],
      [] as (DateTime | undefined)[]
    )
    .filter((x) => x);

  const sorted = sortDateTimes(lastSyncTimes);
  return sorted.length > 0 ? sorted[sorted.length - 1] : undefined;
};

export const useSmartDiaryEntries = (): {
  loading: boolean;
  error: ApolloError | undefined;
  smartEntriesByDay: Record<string, SmartDiaryEntry[]>;
  lastSyncTime?: DateTime;
} => {
  const { currentPeriod } = useSmartTimesheetContext();
  const dateRange = useMemo(
    () => ({
      fromDate: currentPeriod?.startDate ?? defaultDate,
      toDate: currentPeriod?.endDate ?? defaultDate
    }),
    [currentPeriod?.endDate, currentPeriod?.startDate]
  );

  const ranges = useMemo(() => {
    return splitDateRanges(dateRange);
  }, [dateRange]);

  /* 
    Splitting into multiple queries with smaller periods as larger period will have larger response data and its increases network time drastically.  
    As pagination is not possible as we need entire entries for the dates see TIAP-1805 for more details
  */

  // query 1
  const {
    loading: loading1,
    data: result1,
    error,
    refetch: refetch1
  } = usePersistedCacheQuery<
    DiariesForDateRangeQuery,
    DiariesForDateRangeQueryVariables
  >(DiariesForDateRangeDocument, {
    variables: {
      dateRange: ranges[0]
    },
    skip: !currentPeriod,
    nextFetchPolicy: 'cache-first'
  });

  // query 2
  const {
    loading: loading2,
    data: result2,
    refetch: refetch2
  } = usePersistedCacheQuery<
    DiariesForDateRangeQuery,
    DiariesForDateRangeQueryVariables
  >(DiariesForDateRangeDocument, {
    variables: {
      dateRange: ranges[1]
    },
    skip: !currentPeriod || ranges.length < 2,
    nextFetchPolicy: 'cache-first'
  });

  // query 3
  const {
    loading: loading3,
    data: result3,
    refetch: refetch3
  } = usePersistedCacheQuery<
    DiariesForDateRangeQuery,
    DiariesForDateRangeQueryVariables
  >(DiariesForDateRangeDocument, {
    variables: {
      dateRange: ranges[2]
    },
    skip: !currentPeriod || ranges.length < 3,
    nextFetchPolicy: 'cache-first'
  });

  const diaries = useMemo(() => {
    if (ranges.length === 3) {
      if (
        result1?.diariesForDateRange.diaries &&
        result2?.diariesForDateRange.diaries &&
        result3?.diariesForDateRange.diaries
      ) {
        return (result1?.diariesForDateRange.diaries || [])
          .concat(result2?.diariesForDateRange.diaries || [])
          .concat(result3?.diariesForDateRange.diaries || []);
      }
    }
    if (ranges.length === 2) {
      if (
        result1?.diariesForDateRange.diaries &&
        result2?.diariesForDateRange.diaries
      ) {
        return (result1?.diariesForDateRange.diaries || []).concat(
          result2?.diariesForDateRange.diaries || []
        );
      }
    }
    if (ranges.length === 1) return result1?.diariesForDateRange.diaries;
  }, [
    result1?.diariesForDateRange.diaries,
    result2?.diariesForDateRange.diaries,
    result3?.diariesForDateRange.diaries,
    ranges
  ]);
  const notApplicableMessage = useMessage('smartDiary.na');

  const smartEntries = useMemo(
    () =>
      [...(diaries ?? [])].reverse().reduce(
        (acc, value) => ({
          ...acc,
          [value.date]: getSmartDiaryNotes({
            diaryEntries: value.entries as [DiaryEntry],
            notApplicableMessage,
            date: value.date,
            taskNameFormat: value.taskNameFormat
          })
        }),
        {} as Record<string, SmartDiaryEntry[]>
      ),
    [diaries, notApplicableMessage]
  );

  const lastSyncTime = useMemo(() => {
    return getRecentLastSyncTime(diaries as any);
  }, [diaries]);

  const loading = useMemo(() => loading1 || loading2 || loading3, [
    loading1,
    loading2,
    loading3
  ]);

  //#region START auto refresh in case of auto pilot
  const [
    getUpdatedEntries,
    { data: updatedData }
  ] = useDiariesForDateRangeLazyQuery({
    fetchPolicy: 'no-cache'
  });

  const [reloadInterval, setReloadInterval] = useState(
    DEFAULT_POLLING_INTERVAL
  );
  const pollingCount = useRef(0);
  const trackPolling = useRef(new Set());
  const isPageVisible = usePageVisibility();
  const { reloadTimesheet } = useTimesheetFrame();

  const { isAutoPilotEnabled } = useAutoPilotContext();
  useEffect(() => {
    let interval: any = null;
    if (isAutoPilotEnabled && isPageVisible) {
      interval = setInterval(() => {
        pollingCount.current += 1;
        getUpdatedEntries({ variables: { dateRange } });
      }, reloadInterval);
      return () => {
        clearInterval(interval);
      };
    }
  }, [
    isAutoPilotEnabled,
    getUpdatedEntries,
    dateRange,
    reloadInterval,
    isPageVisible
  ]);

  useEffect(() => {
    if (updatedData) {
      const newLastSyncTime = getRecentLastSyncTime(
        updatedData.diariesForDateRange.diaries
      );

      if (newLastSyncTime?.toISO() !== lastSyncTime?.toISO()) {
        refetch1();
        refetch2?.();
        refetch3?.();
        reloadTimesheet();
        pollingCount.current = 0;
        setReloadInterval(DEFAULT_POLLING_INTERVAL);
      } else {
        if (pollingCount.current >= 5) {
          if (
            pollingCount.current % 5 === 0 &&
            !trackPolling.current.has(pollingCount.current)
          ) {
            // after every 5 wasted polling interval increase polling delay by 50%
            trackPolling.current.add(pollingCount.current);
            setReloadInterval((n) =>
              Math.min(
                5 * DEFAULT_POLLING_INTERVAL,
                n + DEFAULT_POLLING_INTERVAL / 2
              )
            );
          }
        }
        if (!isPageVisible) {
          pollingCount.current = 0;
          setReloadInterval(DEFAULT_POLLING_INTERVAL);
        }
      }
    }
  }, [
    isPageVisible,
    lastSyncTime,
    refetch1,
    refetch2,
    refetch3,
    reloadTimesheet,
    updatedData
  ]);
  //#endregion

  return useMemo(
    () => ({
      loading: loading,
      error,
      smartEntriesByDay: smartEntries,
      lastSyncTime: lastSyncTime
    }),
    [loading, error, smartEntries, lastSyncTime]
  );
};
